import React from 'react'
import { GlobalHead } from '@moonpig/web-core-brand/components'
import {
  CookieCategory,
  getBrowserCookies,
  setBrowserCookie,
} from '@moonpig/web-core-browser-storage'
import { GraphQLProvider } from '@moonpig/web-core-graphql'
import {
  BrowserErrorTracker,
  Metrics,
  createMetrics,
} from '@moonpig/web-core-monitoring'
import { isServer } from '@moonpig/web-core-utils'
import { AppContext as NextAppContext } from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import NProgress from 'nprogress'
import { v4 as uuid } from 'uuid'
import { createAuthenticatedFetch } from '@moonpig/web-core-fetch'
import { trackExperiments } from '@moonpig/web-core-eppo/browser/trackExperiment/trackExperiment'
import { AppContainer } from './AppContainer'
import { getInitialProps } from './getInitialProps'
import { getGraphQLContext } from './graphQLContext'
import { AppCoreProps, AppInitialProps, Route } from './types'
import { setRouteData } from './routeData'

NProgress.configure({
  minimum: 0.35,
  trickleSpeed: 120,
  template: '<div class="bar" role="bar"></div>',
})

Router.events.on('routeChangeStart', () => {
  NProgress.start()
})
Router.events.on('routeChangeComplete', () => {
  NProgress.done()
})
Router.events.on('routeChangeError', () => NProgress.done())

class AppCore extends React.Component<React.PropsWithChildren<AppCoreProps>> {
  public static async getInitialProps(
    props: Pick<NextAppContext, 'Component' | 'ctx'> & {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params: { [key: string]: any }
      environment: { localHostName: string }
      browserErrorTracker: BrowserErrorTracker
      getCookie: (name: string) => string | undefined
      userAgent: string
      metrics: Metrics
      notifications: React.ReactNode[]
      flagSchema: { [id: string]: { default: boolean } }
      route: Route
    },
  ): Promise<AppInitialProps> {
    return getInitialProps(props)
  }

  public componentDidMount() {
    const { mnpg_ui_events_api_correlation_id: correlationId } =
      getBrowserCookies()
    setBrowserCookie(
      'mnpg_ui_events_api_correlation_id',
      correlationId || uuid(),
      {
        maxAge: 1200, // we want 20 min sliding expiration
        path: '/',
      },
      CookieCategory.STRICTLY_NECESSARY,
    )
  }

  // eslint-disable-next-line react/sort-comp
  public render() {
    const {
      appShellProps,
      browserErrorTracker,
      children,
      enableServerAuth,
      enableThirdPartyScripts,
      flags,
      graphQLInitialState,
      graphQLOptions,
      isMobile,
      notifications,
      pageRequiresLogin,
      pageTracking,
      preview,
      region,
      sessionId,
      loggedIn,
      routeDataToPersist,
      eppoConfiguration,
      trackedServerExperiments,
      eppoDefaultSubjectAttributes,
      eppoExperimentOverrides,
      customerSegments,
    } = this.props

    if (!isServer) {
      if (routeDataToPersist) {
        setRouteData(routeDataToPersist)
      }

      trackExperiments(trackedServerExperiments)

      browserErrorTracker.addMetadata('sessionId', sessionId)
      browserErrorTracker.addMetadata('shortSessionId', sessionId.slice(-8))
    }

    const metrics = createMetrics()

    const authFetch = createAuthenticatedFetch(
      enableServerAuth,
      sessionId,
      metrics,
    )

    const graphQLContext = getGraphQLContext(
      graphQLOptions,
      authFetch.fetch,
      graphQLInitialState,
    )

    return (
      <>
        <GlobalHead />
        <GraphQLProvider context={graphQLContext}>
          <AppContainer
            appShellProps={appShellProps}
            authBaseUrl={graphQLOptions.authBaseUrl}
            browserErrorTracker={browserErrorTracker}
            enableThirdPartyScripts={enableThirdPartyScripts}
            fetch={authFetch.fetch}
            flags={flags}
            isMobile={isMobile}
            notifications={notifications}
            pageRequiresLogin={pageRequiresLogin}
            pageTracking={pageTracking}
            region={region}
            sessionId={sessionId}
            loggedIn={loggedIn}
            eppoConfiguration={eppoConfiguration}
            eppoDefaultSubjectAttributes={eppoDefaultSubjectAttributes}
            eppoExperimentOverrides={eppoExperimentOverrides}
            customerSegments={customerSegments}
          >
            {children}
          </AppContainer>
        </GraphQLProvider>
        <Head>
          {preview && <meta name="robots" content="noindex, nofollow" />}
        </Head>
      </>
    )
  }
}

export { AppCore }
